import { ScrollingModule } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FwButtonComponent, FwGridComponent, FwGridItemComponent, FwIconComponent } from '@flywheel-io/vision';
import { injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { interval, map, startWith, Subscription } from 'rxjs';

import { AdminModule } from 'app/src/admin/admin.module';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PermissionService } from 'app/src/permission.service';
import { SessionModule } from 'app/src/sessions/session.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { ConfigService } from '../../../config.service';
import { CoreApiService } from '../../../core-api.service';
import { LoggingService } from '../../../logging.service';
import { NotificationService } from '../../../notification.service';
import { PermissionAction } from '../../../shared/enums/permission-action.enum';
import { Project } from '../../../shared/models/project.model';
import { MarkdownService } from '../../../viewers/markdown.service';
import { ViewerService } from '../../../viewers/viewer.service';
import { ProjectStatisticsComponent } from '../../project-statistics/project-statistics.component';
import { ProjectsService } from '../../projects.service';
import { ProjectsQueryService } from '../../projects-query.service';

@Component({
  selector: 'project-description-tab',
  templateUrl: './project-description-tab.component.html',
  styleUrls: ['./project-description-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AdminModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwButtonComponent,
    FwGridComponent,
    FwGridItemComponent,
    FwIconComponent,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    ScrollingModule,
    SessionModule,
    SharedModule,
    RouterModule,
    ProjectStatisticsComponent,
  ],
})
export class ProjectDescriptionTabComponent implements OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private projectsService = inject(ProjectsService);
  private markdownService = inject(MarkdownService);
  private viewerService = inject(ViewerService);
  private coreApi = inject(CoreApiService);
  private notification = inject(NotificationService);
  private logger = inject(LoggingService);
  private projectQueryService = inject(ProjectsQueryService);
  private permissionQueryService = inject(PermissionService);
  private queryClient = inject(QueryClient);
  private config = inject(ConfigService);

  ohifEnabled = this.viewerService.isOhifViewerEnabled();

  constructor() {
    this.subscriptions.refresh = this.refetchInterval$.subscribe(() => {
      this.queryClient.invalidateQueries({
        queryKey: ['project', this.projectId()],
      });
    });
  }

  ngOnDestroy(): void {
    for (const sub of Object.values(this.subscriptions)) {
      sub.unsubscribe();
    }
  }

  descriptionHtml = '';
  editingDescription = false;

  projectDescription = new FormControl('', [Validators.maxLength(10000)]);

  // The aggregate stats update via a worker job, so it is indeterminate when they will update
  // to alleviate this we refetch on an interval to get updated stats.
  // TODO: Remove this if/when we switch to tanstack query or otherwise adjust out caching strategy
  refetchIntervalMilliseconds = 10 * 1000;
  refetchInterval$ = interval(this.refetchIntervalMilliseconds).pipe(
    startWith(0), // emit immediately for initial load
  );

  readonly projectId = toSignal(this.activatedRoute.parent.params.pipe(map((params) => params['projectId'])), {
    requireSync: true,
  });

  private projectQuery = this.projectQueryService.injectProjectQuery(this.projectId);

  readonly project = computed(() => {
    const project = this.projectQuery.data();
    if (!this.editingDescription && project) {
      // ignore upstream changes while editing
      this.descriptionHtml = this.markdownService.render(project.description || '');
      this.projectDescription.patchValue(project.description);
    }
    return project;
  });

  private permissionQuery = injectQuery(() =>
    this.permissionQueryService.permissionsForProjectQueryOptions(this.projectId),
  );

  readonly canEditDescription = computed(() => {
    return (this.permissionQuery.data() || []).includes(PermissionAction.ContainersModifyMetadata);
  });

  private subscriptions = {
    refresh: new Subscription(),
  };

  viewProjectStudies(): void {
    const projectId = this.activatedRoute.snapshot.parent.params.projectId;
    this.viewerService.ohifViewerDialog({
      projectId,
    });
  }

  saveProjectDescription(project: Project): void {
    this.coreApi.projects.put(project._id, { description: this.projectDescription.value }).subscribe({
      next: () => {
        this.notification.success('projectAdmin.updateDescription.success');
        this.editingDescription = false;
        // update the rendered description
        this.descriptionHtml = this.markdownService.render(this.projectDescription.value);
        // clears cache
        this.queryClient.invalidateQueries({
          queryKey: ['project', project._id],
        });
      },
      error: (error) => {
        this.notification.error('projectAdmin.updateDescription.error');
        this.logger.error(error);
        this.projectsService.reloadProject$.next();
      },
    });
  }
}
